/**
 * Play in Editor (SPEC v2, reference 05), like Unreal's PIE: the level's game
 * mode takes the viewport, in Game View, until play stops. The editor stays
 * around it: the Outliner and Inspector still read the level, and the log
 * shows what the game did.
 *
 * The level is kept as it was: while playing, a Command Bus gate refuses
 * edits (and history steps, which would edit it), and stopping gives back the
 * editor's view and Game View setting. Showcase only moves the view, so there
 * is nothing else to put back; a mode that moves entities would need play to
 * run on a copy of the world, as Unreal's PIE does.
 *
 * The game mode is runtime code (src/game); this session is the editor's
 * side: it finds the level's mode, gives it camera poses, ticks it each
 * frame and puts its view in the viewport.
 */
import { parsePayload } from "../../commands/payload";
import type {
  AsyncCommandDefinition,
  CommandDefinition,
  CommandRequest,
} from "../../commands/types";
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import type { Log } from "../../core/Log";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import type { GameModeInstance, GameStop, ViewPose } from "../../game/GameMode";
import type { GameModeRegistry } from "../../game/GameModeRegistry";
import type { EntityId } from "../../world/entity";
import { levelRootOf } from "../../world/hierarchy";
import type { TransformSystem } from "../../world/TransformSystem";
import type { WorldReader } from "../../world/World";
import type { SelectionState } from "../selection/Selection";
import type { SettingsValues } from "../settings/EditorSettings";
import { poseFromMatrix } from "../viewport/cameraMath";
import type { ViewCamera } from "../viewport/cameraMath";
import type { ViewportCamera } from "../viewport/ViewportCamera";

/** What the play UI shows: the mode, the level, the clock and where the view is. */
export interface PlayState {
  readonly mode: string;
  /** The level's name, for the status bar. */
  readonly level: string;
  readonly paused: boolean;
  /** Play time in whole seconds (the toolbar's PIE clock). */
  readonly seconds: number;
  readonly stops: readonly GameStop[];
  readonly current: number;
  readonly autoAdvance: boolean;
}

export interface PlaySessionDeps {
  readonly world: WorldReader;
  readonly transforms: TransformSystem;
  readonly camera: ViewportCamera;
  readonly selection: ReadableStore<SelectionState>;
  readonly settings: ReadableStore<SettingsValues> & {
    readonly writer: { set(key: "gameView", value: boolean): void };
  };
  readonly gameModes: GameModeRegistry;
  readonly bus: { addGate(gate: (request: CommandRequest) => string | null): () => void };
  readonly log: Log;
  /** Piloting ends when play starts: the game mode has the view. */
  readonly pilot: { stop(): void };
}

/** Commands that would change the level (its content or its history). */
const LEVEL_EDITS = /^(entity\.|level\.reset$|history\.)/;

/** What the editor had before play, to give back after. */
interface Before {
  readonly view: ViewCamera;
  readonly gameView: boolean;
}

export class PlaySession implements ReadableStore<PlayState | null> {
  private readonly store: VersionedStore<PlayState | null>;
  private game: GameModeInstance | null = null;
  private before: Before | null = null;
  private elapsed = 0;
  private removeGate: (() => void) | null = null;
  private readonly unsubscribe: () => void;

  constructor(
    private readonly deps: PlaySessionDeps,
    scheduler: ChangeScheduler,
  ) {
    this.store = new VersionedStore<PlayState | null>(scheduler, null);
    // Opening another level ends play, as loading a map ends PIE; the new level keeps its own view.
    this.unsubscribe = deps.world.events.on("world:replaced", () => {
      this.end(false);
    });
  }

  readonly subscribe = (listener: () => void) => this.store.subscribe(listener);
  readonly getSnapshot = (): PlayState | null => this.store.getSnapshot();

  /** The open level's game mode: the first zone's that's registered, or null. */
  modeOfLevel(): string | null {
    for (const record of this.deps.world.getSnapshot().entities.values()) {
      const mode = record.components.get("Zone")?.gameMode;
      if (typeof mode === "string" && this.deps.gameModes.has(mode)) return mode;
    }
    return null;
  }

  async start(): Promise<void> {
    const mode = this.modeOfLevel();
    if (mode === null) throw new Error("This level has no game mode");
    const module = await this.deps.gameModes.load(mode);

    const { camera, settings, selection, world } = this.deps;
    this.deps.pilot.stop();
    this.before = { view: camera.getSnapshot(), gameView: settings.getSnapshot().gameView };
    this.game = module.create(
      { world, viewOf: (id) => this.viewOf(id) },
      { view: poseOf(camera.getSnapshot()), at: selection.getSnapshot().active },
    );
    this.elapsed = 0;
    this.removeGate = this.deps.bus.addGate(refuseWhilePlaying);
    settings.writer.set("gameView", true);
    this.publish({ mode, level: this.levelName(), paused: false });
    this.logBegin(mode);
  }

  /** Ends play, giving back the editor's view and Game View setting. */
  stop(): void {
    this.end(true);
  }

  private end(restoreView: boolean): void {
    if (!this.game) return;
    const before = this.before;
    this.game = null;
    this.before = null;
    this.removeGate?.();
    this.removeGate = null;
    this.store.publish(null);

    if (before) {
      this.deps.settings.writer.set("gameView", before.gameView);
      if (restoreView) this.deps.camera.navigate(() => before.view);
    }
    this.deps.log.display("PIE", "End play");
  }

  setPaused(paused: boolean): void {
    const state = this.getSnapshot();
    if (state) this.store.publish({ ...state, paused });
  }

  goTo(index: number): void {
    const state = this.getSnapshot();
    if (!this.game || !state) return;
    this.game.goTo(index);
    this.publish(state);
    this.logPossess();
  }

  setAutoAdvance(on: boolean): void {
    const state = this.getSnapshot();
    if (!this.game || !state) return;
    this.game.autoAdvance = on;
    this.publish(state);
  }

  /** One frame of play: the mode moves on (unless paused), and the viewport shows its view. */
  tick(seconds: number): void {
    const state = this.getSnapshot();
    if (!this.game || !state || state.paused) return;

    this.elapsed += seconds;
    const current = this.game.current;
    this.game.tick(seconds);
    // The store changes only when what the UI shows does: the whole second, or the zone.
    if (Math.floor(this.elapsed) !== state.seconds || this.game.current !== current) {
      this.publish(state);
    }

    const view = this.game.view();
    if (view) this.deps.camera.navigate((camera) => withPose(camera, view));
  }

  dispose(): void {
    this.stop();
    this.unsubscribe();
  }

  // ─── Internals ─────────────────────────────────────────────────────────

  /** A camera entity's view: its world pose (camera entities look down -Z) and lens. */
  private viewOf(id: EntityId): ViewPose | null {
    const camera = this.deps.world.component(id, "Camera");
    if (!camera) return null;
    const pose = poseFromMatrix(this.deps.transforms.worldMatrix(id));
    return { ...pose, fov: camera.fov as number };
  }

  private levelName(): string {
    const root = levelRootOf(this.deps.world.getSnapshot().entities);
    return (root && this.deps.world.get(root)?.name) ?? "";
  }

  private publish(base: Pick<PlayState, "mode" | "level" | "paused">): void {
    const game = this.game;
    if (!game) return;
    this.store.publish({
      mode: base.mode,
      level: base.level,
      paused: base.paused,
      seconds: Math.floor(this.elapsed),
      stops: game.stops,
      current: game.current,
      autoAdvance: game.autoAdvance,
    });
  }

  private logPossess(): void {
    const game = this.game;
    const stop = game?.stops[game.current];
    if (game && stop) {
      this.deps.log.display("Camera", `possess ${stop.camera} · blend ${game.blendSeconds}s`);
    }
  }

  /** The Output Log's account of play starting, as reference 05 shows it. */
  private logBegin(mode: string): void {
    const { log } = this.deps;
    log.display("PIE", `Begin play · ${this.levelName()} · GameMode ${mode}`);
    this.logPossess();
    const count = this.game?.stops.length ?? 0;
    const keys = count > 1 ? `1–${Math.min(count, 9)} or ←/→ cycle zones · ` : "";
    log.display("Input", `${keys}Esc stop`);
  }
}

function refuseWhilePlaying(request: CommandRequest): string | null {
  if (LEVEL_EDITS.test(request.type)) return "Stop playing to edit the level";
  // Moving the editor's own view: the game mode has it.
  if (request.type.startsWith("camera.")) return "The game has the camera while playing";
  return null;
}

function poseOf(camera: ViewCamera): ViewPose {
  return { position: camera.position, yaw: camera.yaw, pitch: camera.pitch, fov: camera.fov };
}

function withPose(camera: ViewCamera, view: ViewPose): ViewCamera {
  return { ...camera, ...view, projection: "perspective" };
}

// ─── Commands ────────────────────────────────────────────────────────────

/**
 * `play.start` (async: the mode's code may still be loading), `play.stop`,
 * `play.pause`, `play.goTo` and `play.autoAdvance`. None are undoable: play
 * never changes the level.
 */
export function playCommands(session: PlaySession): {
  readonly sync: CommandDefinition[];
  readonly start: AsyncCommandDefinition<Record<string, never>>;
} {
  const playing = (): { ok: false; error: string } | null =>
    session.getSnapshot() ? null : { ok: false, error: "Not playing" };

  const stop: CommandDefinition<Record<string, never>, undefined> = {
    type: "play.stop",
    undoable: false,
    validate: () => playing() ?? { ok: true, value: {} },
    do: () => {
      session.stop();
      return undefined;
    },
    undo: () => undefined,
    describe: () => "Stop play",
  };
  const pause: CommandDefinition<{ paused: boolean }, undefined> = {
    type: "play.pause",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { paused: "boolean" });
      return shape.ok ? (playing() ?? shape) : shape;
    },
    do: ({ paused }) => {
      session.setPaused(paused);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ paused }) => (paused ? "Pause play" : "Resume play"),
  };
  const goTo: CommandDefinition<{ index: number }, undefined> = {
    type: "play.goTo",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { index: "number" });
      return shape.ok ? (playing() ?? shape) : shape;
    },
    do: ({ index }) => {
      session.goTo(index);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ index }) => `Play: go to ${session.getSnapshot()?.stops[index]?.label ?? index}`,
  };
  const autoAdvance: CommandDefinition<{ on: boolean }, undefined> = {
    type: "play.autoAdvance",
    undoable: false,
    validate: (raw) => {
      const shape = parsePayload(raw, { on: "boolean" });
      return shape.ok ? (playing() ?? shape) : shape;
    },
    do: ({ on }) => {
      session.setAutoAdvance(on);
      return undefined;
    },
    undo: () => undefined,
    describe: ({ on }) => (on ? "Auto-advance on" : "Auto-advance off"),
  };

  const start: AsyncCommandDefinition<Record<string, never>> = {
    type: "play.start",
    validate: () => {
      if (session.getSnapshot()) return { ok: false, error: "Already playing" };
      const mode = session.modeOfLevel();
      return mode === null
        ? { ok: false, error: "This level has no game mode to play" }
        : { ok: true, value: {} };
    },
    run: () => session.start(),
    describe: () => "Play",
  };

  return { sync: [stop, pause, goTo, autoAdvance] as CommandDefinition[], start };
}
